import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { ConnecteeSubmission, SUBMISSION_STATUS } from "../types/connecteeSubmission";
import { fetchConnecteeSubmission } from "../utils/fetchConnecteeSubmission";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes from createdAt

export type PollState = "polling" | "ready" | "timeout" | "error";

interface UseConnecteeSubmissionPollResult {
  submission: ConnecteeSubmission | null;
  pollState: PollState;
  errorMessage: string | null;
}

function reportSubmissionResultError(
  message: string,
  context: {
    reason: "timeout" | "missing_submission";
    submissionId?: number;
    status?: string;
    cause?: unknown;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag("feature", "connectee_submission_result");
    scope.setTag("result_error_reason", context.reason);
    if (context.submissionId != null) {
      scope.setTag("submission_id", String(context.submissionId));
      scope.setExtra("submissionId", context.submissionId);
    }
    if (context.status) {
      scope.setExtra("submissionStatus", context.status);
    }

    if (context.cause instanceof Error) {
      scope.setExtra("causeMessage", context.cause.message);
      Sentry.captureException(context.cause);
      return;
    }

    Sentry.captureMessage(message, "error");
  });
}

export function useConnecteeSubmissionPoll(
  initialSubmission: ConnecteeSubmission | null
): UseConnecteeSubmissionPollResult {
  const [submission, setSubmission] = useState<ConnecteeSubmission | null>(initialSubmission);

  const [pollState, setPollState] = useState<PollState>(() => {
    if (!initialSubmission) return "error";
    if (initialSubmission.status !== SUBMISSION_STATUS.NEW_REQUEST) return "ready";
    return "polling";
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(
    initialSubmission ? null : "Missing submission"
  );

  const cancelledRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const lastPollErrorRef = useRef<unknown>(null);
  const reportedRef = useRef(false);

  // Extract primitives for the dependency array
  const submissionId = initialSubmission?.id;
  const accessToken = initialSubmission?.accessToken;
  const createdAt = initialSubmission?.createdAt;
  const initialStatus = initialSubmission?.status;

  useEffect(() => {
    cancelledRef.current = false;
    reportedRef.current = false;
    lastPollErrorRef.current = null;

    if (!initialSubmission || !submissionId || !initialStatus) {
      setPollState("error");
      setErrorMessage("Missing submission");
      if (!reportedRef.current) {
        reportedRef.current = true;
        reportSubmissionResultError("Connectee submission missing after create", {
          reason: "missing_submission",
        });
      }
      return;
    }

    setSubmission(initialSubmission);

    // Already matched — use payload as-is (dev mocks and post-poll snapshots)
    if (initialStatus !== SUBMISSION_STATUS.NEW_REQUEST) {
      setPollState("ready");
      setErrorMessage(null);
      return;
    }

    if (!accessToken) {
      setPollState("error");
      setErrorMessage("Missing submission");
      if (!reportedRef.current) {
        reportedRef.current = true;
        reportSubmissionResultError("Connectee submission missing after create", {
          reason: "missing_submission",
          submissionId,
          status: initialStatus,
        });
      }
      return;
    }

    setPollState("polling");
    setErrorMessage(null);

    const createdAtMs = createdAt ? new Date(createdAt).getTime() : NaN;
    const deadlineMs = Number.isFinite(createdAtMs)
      ? createdAtMs + POLL_TIMEOUT_MS
      : Date.now() + POLL_TIMEOUT_MS;

    const clearScheduledPoll = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleNext = () => {
      clearScheduledPoll();
      timeoutRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    const poll = async () => {
      if (cancelledRef.current) return;

      if (Date.now() >= deadlineMs) {
        setPollState("timeout");
        setErrorMessage(
          "We're still working on your match. This is taking longer than expected — please email us or try refining your search."
        );
        if (!reportedRef.current) {
          reportedRef.current = true;
          reportSubmissionResultError("Connectee submission matching poll timed out", {
            reason: "timeout",
            submissionId,
            status: initialStatus,
            cause: lastPollErrorRef.current ?? undefined,
          });
        }
        return;
      }

      try {
        const latest = await fetchConnecteeSubmission(accessToken);
        if (cancelledRef.current) return;

        lastPollErrorRef.current = null;
        setSubmission({ ...latest, accessToken });

        if (latest.status !== SUBMISSION_STATUS.NEW_REQUEST) {
          setPollState("ready");
          return;
        }
      } catch (error: unknown) {
        if (cancelledRef.current) return;
        lastPollErrorRef.current = error;
        console.error(error);
        Sentry.addBreadcrumb({
          category: "connectee_submission_poll",
          message: error instanceof Error ? error.message : "Poll request failed",
          level: "warning",
          data: { submissionId },
        });
      }

      if (!cancelledRef.current) {
        scheduleNext();
      }
    };

    scheduleNext();

    return () => {
      cancelledRef.current = true;
      clearScheduledPoll();
    };
  }, [initialSubmission, submissionId, accessToken, initialStatus, createdAt]);

  return { submission, pollState, errorMessage };
}
